// Menú lateral agrupado (puro, con tests): búsqueda, grupos y favoritos.
//
// Lo usa Reportes: doce informes en una sola lista larga, todos empezando con
// "Informe de", obligaban a leer cada renglón. Mapon los separa por tema y deja
// fijar los de uso diario; esto es lo mismo, sin tocar a las demás apps (sólo
// se activa si el layout pasa `grouped`).

export interface GroupableItem {
  id: string;
  label: string;
  description?: string;
  group?: string;
}

export interface MenuGroup<T> {
  group: string;
  items: T[];
}

// Sin acentos ni mayúsculas: "conduccion" debe encontrar "Conducción".
export const normalize = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

// Busca en el nombre, la descripción y el grupo: quien escribe "zona" espera
// ver Geocercas aunque la palabra sólo esté en el nombre del grupo.
export const filterItems = <T extends GroupableItem>(items: T[], query: string): T[] => {
  const q = normalize(query);
  if (!q) return items;
  return items.filter((it) =>
    [it.label, it.description, it.group].some((f) => f && normalize(f).includes(q)),
  );
};

// Agrupa respetando el orden en que el layout declara los items: el orden de
// los grupos lo da el primer item de cada uno.
export const groupItems = <T extends GroupableItem>(items: T[], fallback = ""): MenuGroup<T>[] => {
  const groups = new Map<string, T[]>();
  for (const it of items) {
    const g = it.group ?? fallback;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(it);
  }
  return [...groups].map(([group, list]) => ({ group, items: list }));
};

// Favoritos en el orden en que se fijaron; uno que el layout ya no muestra
// (sin permiso o deshabilitado en la config) simplemente no aparece.
export const resolveMenuFavorites = <T extends { id: string }>(favs: string[], items: T[]): T[] => {
  const byId = new Map(items.map((i) => [i.id, i]));
  return favs.map((id) => byId.get(id)).filter((i): i is T => !!i);
};

export const MENU_FAVORITES_EVENT = "dumax:menu-favorites-changed";

export const menuFavoritesKey = (scope: string, userId: string | null | undefined) =>
  `dumax.menuFavorites.v1.${scope}.${userId || "anon"}`;

export const parseMenuFavorites = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
};

export const toggleMenuFavorite = (favs: string[], id: string): string[] =>
  favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id];
